import { Page } from '~/models/PaginatedItems'
import { BaseMemoryCache, CacheEntry, Entity } from './baseMemoryCache'
import { PaginationService } from './shared/pagination'

type PagedCacheEntry = CacheEntry<string[]> & {
	extradata: { count: number }
}

export class BasePaginatedMemoryCache<T> extends BaseMemoryCache<T> {
	getPage(key: string, page: number, pageSize: number): Page<T> | undefined {
		const cachedPage = this.cache.get(key) as PagedCacheEntry
		if (!cachedPage) return

		if (Date.now() > cachedPage.expiresAt) {
			this.cache.delete(key)
			return
		}

		const start = page * pageSize
		const pageIds = cachedPage.value.slice(start, start + pageSize)
		const hasUndefined = pageIds.some((item) => !item)
		if (hasUndefined) return

		const entities = this.getMany(cachedPage.value as string[])

		const cachedItems = PaginationService.getItems(
			entities,
			page,
			cachedPage.extradata.count,
			pageSize
		)

		const hasNext = (page + 1) * pageSize < cachedPage.extradata.count
		const next = hasNext ? page + 1 : undefined
		return { ...cachedItems, next }
	}

	setPage(key: string, page: Page<Entity<T>>, durationMs?: number): void {
		const duration = durationMs ?? this.defaultDurationMs
		const expiresAt = Date.now() + duration

		const current = this.cache.get(key) as PagedCacheEntry | undefined
		let ids = current?.value
		if (!ids) ids = new Array(page.count!).fill(undefined)

		const start = page.page! * page.pageSize!
		page.items.forEach((item, i) => (ids[start + i] = item.id!))

		const entry: PagedCacheEntry = { expiresAt, value: ids, extradata: { count: page.count! } }
		this.cache.set(key, entry)
		this.setMany(page.items)
	}
}
